import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";

const CLASSES = ["D1", "D2", "D3", "D4", "D5"];
const KIND_LABELS = {
  class: "Per vehicle class (fixed)",
  class_starting: "Per class (starting at…)",
  flat: "Flat (fixed)",
  flat_starting: "Flat (starting at…)",
};
const inp = "bg-black/40 border border-white/12 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#1F5EFF]";

/**
 * Website catalog manager: services (D1–D5 class pricing) + packages (bundles).
 * This is the SINGLE source of truth — the public site, calculator, quick book
 * and (via auto-sync) the internal CRM catalog all read this config.
 */
export default function ServicesPricing() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data: c } = await api.get("/crm/pricing");
    setCfg(c);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/crm/pricing", {
        services: cfg.services, addons: cfg.addons, booking: cfg.booking, promo: cfg.promo,
        bundles: cfg.bundles || [], upsell_rules: cfg.upsell_rules || [], followup_rules: cfg.followup_rules || [],
      });
      setCfg(data);
      toast.success(`Saved (v${data.version}) — site updated instantly, internal catalog synced`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-10 text-white/40"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  const updateService = (key, patch) =>
    setCfg({ ...cfg, services: cfg.services.map((s) => (s.key === key ? { ...s, ...patch } : s)) });

  return (
    <div className="space-y-8" data-testid="services-pricing">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl text-white">Website Services &amp; Prices</h2>
          <p className="text-white/40 text-xs mt-1">
            Prices per vehicle class D1–D5 (D2 = average sedan). These exact names and prices power the website,
            quick book and calculator — and auto-sync to the internal catalog below. Leave a class empty for "Custom Quote".
          </p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-services-pricing"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save All Changes
        </button>
      </div>

      {/* Online booking rules */}
      <section className="bg-[#121216] border border-white/12 rounded-2xl p-6" data-testid="booking-rules">
        <div className="text-white/50 font-mono-alt text-[10px] mb-1 tracking-widest">ONLINE BOOKING RULES</div>
        <p className="text-white/40 text-xs mb-4">
          A time slot stays bookable while fewer than this many in-progress bookings overlap the full service duration
          (a booking blocks its whole time, not just the start hour). Bays: 3 — the cap can be lower, never higher.
        </p>
        <label className="flex items-center gap-3 text-sm text-white/70">
          Max simultaneous bookings:
          <input
            type="number" min="1" max="3"
            value={cfg.booking?.max_concurrent ?? 2}
            onChange={(e) => setCfg({ ...cfg, booking: { ...(cfg.booking || {}), max_concurrent: Math.max(1, Math.min(3, Number(e.target.value) || 1)) } })}
            className={`${inp} w-20 text-center tabular-nums`}
            data-testid="booking-max-concurrent"
          />
        </label>
      </section>

      {/* Services */}
      {cfg.services.map((s) => (
        <section key={s.key} className={`bg-[#121216] border rounded-2xl p-6 ${s.active ? "border-white/12" : "border-white/5 opacity-60"}`}
          data-testid={`pricing-service-${s.key}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <input value={s.name} onChange={(e) => updateService(s.key, { name: e.target.value })}
              className="bg-transparent text-white font-display text-xl outline-none border-b border-transparent focus:border-white/20" />
            <div className="flex items-center gap-4">
              <select value={s.price_kind} onChange={(e) => updateService(s.key, { price_kind: e.target.value })}
                className={inp} data-testid={`pricing-kind-${s.key}`}>
                {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-white/60">
                <input type="checkbox" checked={!!s.active}
                  onChange={(e) => updateService(s.key, { active: e.target.checked })}
                  className="w-3.5 h-3.5 accent-[#1F5EFF]" data-testid={`pricing-active-${s.key}`} />
                Active
              </label>
              <label className="flex items-center gap-1.5 text-xs text-amber-300/80">
                <input type="checkbox" checked={!!s.featured}
                  onChange={(e) => updateService(s.key, { featured: e.target.checked })}
                  className="w-3.5 h-3.5 accent-amber-400" data-testid={`pricing-featured-${s.key}`} />
                Most Popular
              </label>
            </div>
          </div>

          <textarea value={s.description || ""} onChange={(e) => updateService(s.key, { description: e.target.value })}
            rows={2} className={`${inp} w-full mb-4`} placeholder="Customer-facing description" />

          {s.options?.length > 0 && (
            <div className="mb-4">
              <div className="text-white/50 font-mono-alt text-[10px] mb-2">FIXED-PRICE OPTIONS (price is the same for all vehicle classes)</div>
              <div className="space-y-2">
                {s.options.map((o, i) => (
                  <div key={o.key} className="flex items-center gap-3">
                    <input value={o.label}
                      onChange={(e) => updateService(s.key, { options: s.options.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x) })}
                      className={`${inp} flex-1`} data-testid={`pricing-${s.key}-opt-${o.key}-label`} />
                    <input type="number" min="0" value={o.price ?? ""}
                      onChange={(e) => updateService(s.key, { options: s.options.map((x, xi) => xi === i ? { ...x, price: Number(e.target.value) || 0 } : x) })}
                      className={`${inp} w-28 tabular-nums`} data-testid={`pricing-${s.key}-opt-${o.key}-price`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {s.price_kind.startsWith("class") ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {CLASSES.map((c) => (
                <label key={c} className="block">
                  <span className="text-[10px] font-mono-alt text-white/40 uppercase">{c}</span>
                  <input type="number" min="0" value={s.prices?.[c] ?? ""}
                    onChange={(e) => updateService(s.key, { prices: { ...s.prices, [c]: e.target.value === "" ? null : Number(e.target.value) } })}
                    className={`${inp} w-full mt-1 tabular-nums`} data-testid={`pricing-${s.key}-${c}`} placeholder="Custom" />
                </label>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <label className="text-white/50 font-mono-alt text-[10px]">PRICE $</label>
              <input type="number" min="0" value={s.flat_price ?? ""}
                onChange={(e) => updateService(s.key, { flat_price: e.target.value === "" ? null : Number(e.target.value) })}
                className={`${inp} w-28 tabular-nums`} data-testid={`pricing-${s.key}-flat`} />
              <input value={s.unit || ""} onChange={(e) => updateService(s.key, { unit: e.target.value })}
                className={`${inp} w-40`} placeholder="unit (pair / set of 4)" />
            </div>
          )}
          <div className="mt-3 flex items-center gap-3">
            <label className="text-white/50 font-mono-alt text-[10px]">DURATION (MIN)</label>
            <input type="number" min="15" step="15" value={s.duration_min ?? 60}
              onChange={(e) => updateService(s.key, { duration_min: Number(e.target.value) || 60 })}
              className={`${inp} w-24 tabular-nums`} data-testid={`pricing-${s.key}-duration`} />
            <span className="text-white/30 text-xs">≈ {(((s.duration_min ?? 60)) / 60).toFixed(1)} h — reserves one bay in online booking</span>
          </div>
          <input value={s.note || ""} onChange={(e) => updateService(s.key, { note: e.target.value })}
            className={`${inp} w-full mt-3`} placeholder="Note shown under the service (disclaimers…)" />
        </section>
      ))}

      {/* Service packages (bundles) */}
      <section className="bg-[#121216] border border-[#1F5EFF]/25 rounded-2xl p-6" data-testid="bundles-settings">
        <div className="flex items-center justify-between mb-1">
          <div className="font-mono-alt text-[11px] text-[#1F5EFF]">Popular Packages</div>
          <button type="button"
            onClick={() => setCfg({
              ...cfg,
              bundles: [...(cfg.bundles || []), {
                key: `bundle-${Date.now().toString(36)}`, name: "New Package", tagline: "",
                services: [], addon_flags: [], discount_percent: 10, active: true,
                order: (cfg.bundles || []).length + 1,
              }],
            })}
            data-testid="bundle-add"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1F5EFF] text-white text-xs font-medium hover:bg-white hover:text-black transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add package
          </button>
        </div>
        <p className="text-white/40 text-xs mb-5">
          Ready-made service combinations in the calculator. Prices are computed live from service prices —
          never fixed here. The package discount applies only when the Grand Opening promo is off (they never stack).
        </p>
        <div className="space-y-4">
          {(cfg.bundles || []).map((b, bi) => (
            <div key={b.key} className={`rounded-xl border p-4 ${b.active ? "border-white/12 bg-black/30" : "border-white/5 opacity-60"}`}
              data-testid={`bundle-editor-${b.key}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <input value={b.name}
                  onChange={(e) => setCfg({ ...cfg, bundles: cfg.bundles.map((x, xi) => xi === bi ? { ...x, name: e.target.value } : x) })}
                  className={`${inp} flex-1 min-w-[180px] font-medium`} data-testid={`bundle-name-${b.key}`} />
                <label className="flex items-center gap-1.5 text-xs text-white/60">
                  <span className="font-mono-alt text-[10px] text-white/40">SAVE %</span>
                  <input type="number" min="1" max="90" value={b.discount_percent ?? 10}
                    onChange={(e) => setCfg({ ...cfg, bundles: cfg.bundles.map((x, xi) => xi === bi ? { ...x, discount_percent: Number(e.target.value) || 10 } : x) })}
                    className={`${inp} w-16 tabular-nums`} data-testid={`bundle-pct-${b.key}`} />
                </label>
                <label className="flex items-center gap-1.5 text-xs text-white/60">
                  <span className="font-mono-alt text-[10px] text-white/40">ORDER</span>
                  <input type="number" min="1" value={b.order ?? 99}
                    onChange={(e) => setCfg({ ...cfg, bundles: cfg.bundles.map((x, xi) => xi === bi ? { ...x, order: Number(e.target.value) || 99 } : x) })}
                    className={`${inp} w-16 tabular-nums`} data-testid={`bundle-order-${b.key}`} />
                </label>
                <label className="flex items-center gap-1.5 text-xs text-white/60">
                  <input type="checkbox" checked={!!b.active}
                    onChange={(e) => setCfg({ ...cfg, bundles: cfg.bundles.map((x, xi) => xi === bi ? { ...x, active: e.target.checked } : x) })}
                    className="w-3.5 h-3.5 accent-[#1F5EFF]" data-testid={`bundle-active-${b.key}`} />
                  Active
                </label>
                {b.seasonal && (
                  <span className="px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 font-mono-alt text-[9px] uppercase tracking-widest"
                    data-testid={`bundle-seasonal-${b.key}`}>
                    Seasonal — toggle with the season
                  </span>
                )}
                <button type="button"
                  onClick={() => setCfg({ ...cfg, bundles: cfg.bundles.filter((_, xi) => xi !== bi) })}
                  className="p-1.5 text-white/40 hover:text-red-400" data-testid={`bundle-del-${b.key}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <input value={b.tagline || ""}
                onChange={(e) => setCfg({ ...cfg, bundles: cfg.bundles.map((x, xi) => xi === bi ? { ...x, tagline: e.target.value } : x) })}
                className={`${inp} w-full mb-3`} placeholder="Short tagline (customer-facing)"
                data-testid={`bundle-tagline-${b.key}`} />
              <div className="flex flex-wrap gap-1.5">
                {cfg.services.filter((s) => s.active).map((s) => {
                  const on = (b.services || []).includes(s.key);
                  return (
                    <button key={s.key} type="button"
                      onClick={() => setCfg({
                        ...cfg,
                        bundles: cfg.bundles.map((x, xi) => xi === bi
                          ? { ...x, services: on ? x.services.filter((k) => k !== s.key) : [...(x.services || []), s.key] }
                          : x),
                      })}
                      data-testid={`bundle-svc-${b.key}-${s.key}`}
                      className={`px-2.5 py-1 rounded-lg border text-[11px] transition-colors ${
                        on ? "bg-[#1F5EFF]/20 border-[#1F5EFF]/60 text-white" : "border-white/12 text-white/45 hover:border-white/30"}`}>
                      {s.name}
                    </button>
                  );
                })}
              </div>
              {Object.values(cfg.addons || {}).some((a) => a.mode === "flag") && (
                <>
                  <div className="mt-3 mb-1.5 text-[10px] font-mono-alt text-white/40 uppercase">
                    Included add-ons (optional — e.g. 1-Year Ceramic with Paint Correction; the package % applies to services only)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.values(cfg.addons || {}).filter((a) => a.mode === "flag").map((a) => {
                      const on = (b.addon_flags || []).includes(a.key);
                      return (
                        <button key={a.key} type="button"
                          onClick={() => setCfg({
                            ...cfg,
                            bundles: cfg.bundles.map((x, xi) => xi === bi
                              ? { ...x, addon_flags: on ? (x.addon_flags || []).filter((k) => k !== a.key) : [...(x.addon_flags || []), a.key] }
                              : x),
                          })}
                          data-testid={`bundle-addon-${b.key}-${a.key}`}
                          className={`px-2.5 py-1 rounded-lg border text-[11px] transition-colors ${
                            on ? "bg-emerald-400/15 border-emerald-400/50 text-emerald-200" : "border-white/12 text-white/45 hover:border-white/30"}`}>
                          + {a.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))}
          {(cfg.bundles || []).length === 0 && (
            <div className="text-white/30 text-sm italic">No packages yet.</div>
          )}
        </div>
      </section>
    </div>
  );
}
